import { useEffect, useMemo, useState } from 'react';
import { api, ErroApi } from '../../api';
import {
  Campo, Carregando, Erro, Modal, Selo, Tabela, Vazio, cnpjFormatado, mascaraCnpj,
} from '../../componentes/ui';
import type { Empresa, Escritorio } from '../../tipos';

export function Empresas() {
  const [lista, setLista] = useState<Empresa[] | null>(null);
  const [escritorios, setEscritorios] = useState<Escritorio[]>([]);
  const [busca, setBusca] = useState('');
  const [filtroEscritorio, setFiltroEscritorio] = useState<number | ''>('');
  const [erro, setErro] = useState('');
  const [nova, setNova] = useState(false);

  async function carregar() {
    try {
      const { empresas } = await api.empresas({
        busca: busca || undefined,
        escritorio: filtroEscritorio || undefined,
      });
      setLista(empresas);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as empresas.');
    }
  }

  useEffect(() => { api.escritorios().then((r) => setEscritorios(r.escritorios)).catch(() => {}); }, []);

  // Busca com respiro: filtrar a cada tecla dispararia uma consulta por
  // letra digitada, e a lista pode passar de quinhentas empresas.
  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, filtroEscritorio]);

  const automaticas = useMemo(() => (lista || []).filter((e) => e.origem === 'auto').length, [lista]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Empresas</h1>
          <p>
            Quem emite. Não precisa cadastrar antes: quando um CNPJ novo aparece num XML, o portal
            cria a empresa no escritório da instalação, com a razão social lida do próprio documento.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setNova(true)}>Nova empresa</button>
      </header>

      <div className="filtros">
        <input
          className="input"
          placeholder="Buscar por razão social ou CNPJ…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className="input"
          value={filtroEscritorio}
          onChange={(e) => setFiltroEscritorio(e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">Todos os escritórios</option>
          {escritorios.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      </div>

      <Erro>{erro}</Erro>

      {!lista ? <Carregando /> : lista.length === 0 ? (
        <Vazio
          titulo={busca || filtroEscritorio ? 'Nada com esse filtro' : 'Nenhuma empresa ainda'}
          descricao={
            busca || filtroEscritorio
              ? 'Tente outro trecho da razão social ou do CNPJ.'
              : 'A primeira nota que chegar de um CNPJ desconhecido já cadastra a empresa sozinha.'
          }
        />
      ) : (
        <>
          {automaticas > 0 && (
            <p className="nota">
              {automaticas} desta lista {automaticas === 1 ? 'nasceu' : 'nasceram'} de um XML.
              Vale conferir se a razão social e o escritório estão certos.
            </p>
          )}
          <Tabela
            itens={lista}
            chaveDe={(e) => e.id}
            classeLinha={(e) => (e.ativo ? undefined : 'linha-inativa')}
            colunas={[
              {
                rotulo: 'Razão social',
                principal: true,
                celula: (e) => (
                  <>
                    <strong>{e.razaoSocial}</strong>
                    {e.expurgoBloqueado && <span className="selo atencao" style={{ marginLeft: 8 }}>expurgo travado</span>}
                  </>
                ),
              },
              { rotulo: 'CNPJ', fraco: true, mono: true, celula: (e) => cnpjFormatado(e.cnpj) },
              { rotulo: 'Escritório', fraco: true, celula: (e) => e.escritorio },
              { rotulo: 'Origem', celula: (e) => <Selo tipo={e.origem} /> },
              { rotulo: 'Situação', acoes: true, celula: (e) => (!e.ativo ? <Selo tipo="inativo" /> : null) },
            ]}
          />
        </>
      )}

      {nova && (
        <NovaEmpresa
          escritorios={escritorios}
          onFechar={() => setNova(false)}
          onCriada={() => { setNova(false); void carregar(); }}
        />
      )}
    </div>
  );
}

function NovaEmpresa({
  escritorios, onFechar, onCriada,
}: { escritorios: Escritorio[]; onFechar: () => void; onCriada: () => void }) {
  const [cnpj, setCnpj] = useState('');
  const [razao, setRazao] = useState('');
  const [escritorioId, setEscritorioId] = useState<number | ''>(escritorios[0]?.id ?? '');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setErro('');
    setSalvando(true);
    try {
      await api.criarEmpresa({
        cnpj: cnpj.replace(/\D/g, ''),
        razaoSocial: razao,
        escritorioId: Number(escritorioId),
      });
      onCriada();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      titulo="Nova empresa"
      descricao="Só é preciso quando você quer a empresa cadastrada antes da primeira nota chegar."
      onFechar={onFechar}
    >
      <Erro>{erro}</Erro>
      <Campo rotulo="CNPJ">
        <input className="input" value={cnpj} onChange={(e) => setCnpj(mascaraCnpj(e.target.value))}
          placeholder="00.000.000/0000-00" inputMode="numeric" autoFocus />
      </Campo>
      <Campo rotulo="Razão social">
        <input className="input" value={razao} onChange={(e) => setRazao(e.target.value)}
          placeholder="Comércio Vale Verde LTDA" />
      </Campo>
      <Campo rotulo="Escritório" ajuda="Quem vai enxergar os documentos desta empresa.">
        <select className="input" value={escritorioId}
          onChange={(e) => setEscritorioId(e.target.value ? Number(e.target.value) : '')}>
          <option value="">Escolha…</option>
          {escritorios.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      </Campo>

      <div className="modal-acoes">
        <button className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button
          className="btn btn-primary"
          onClick={salvar}
          disabled={salvando || cnpj.replace(/\D/g, '').length !== 14 || razao.trim().length < 2 || !escritorioId}
        >
          {salvando ? <span className="spinner" /> : 'Criar empresa'}
        </button>
      </div>
    </Modal>
  );
}
